
import React from 'react';

import {

  useNodesState,
  useEdgesState,
  Node,
  Edge,
} from '@xyflow/react';

import '@xyflow/react/dist/style.css';
import { Cloud } from 'lucide-react';
import ProjectCaseStudy from '../components/ProjectCaseStudy';

const initialNodes: Node[] = [
  {
    id: '1',
    data: { label: 'Get content from Notion' },
    position: { x: 100, y: 150 },
    style: {
      background: '#18181b',
      color: 'white',
      border: '1px solid #7c3aed',
      borderRadius: '12px',
      fontSize: '12px',
      fontWeight: 'bold',
      padding: '12px',
    },
  },
  {
    id: '2',
    data: { label: 'Get content from AI Agent' },
    position: { x: 100, y: 250 },
    style: {
      background: '#18181b',
      color: 'white',
      border: '1px solid #7c3aed',
      borderRadius: '12px',
      fontSize: '12px',
      fontWeight: 'bold',
      padding: '12px',
    },
  },
  {
    id: '3',
    data: { label: 'Process Content' },
    position: { x: 350, y: 200 },
    style: {
      background: '#18181b',
      color: 'white',
      border: '1px solid #7c3aed',
      borderRadius: '12px',
      fontSize: '12px',
      fontWeight: 'bold',
      padding: '12px',
    },
  },
  {
    id: '4',
    data: { label: 'Review Decision' },
    position: { x: 300, y: 350 },
    style: {
      background: '#18181b',
      color: 'white',
      border: '1px solid #7c3aed',
      borderRadius: '50px',
      fontSize: '12px',
      fontWeight: 'bold',
      padding: '15px',
      width: '120px',
      height: '80px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      textAlign: 'center',
    },
  },
  {
    id: '5',
    data: { label: 'Post on LinkedIn' },
    position: { x: 500, y: 280 },
    style: {
      background: '#18181b',
      color: 'white',
      border: '1px solid #7c3aed',
      borderRadius: '12px',
      fontSize: '12px',
      fontWeight: 'bold',
      padding: '12px',
    },
  },
  {
    id: '6',
    data: { label: 'Update Notion Column' },
    position: { x: 500, y: 380 },
    style: {
      background: '#18181b',
      color: 'white',
      border: '1px solid #7c3aed',
      borderRadius: '12px',
      fontSize: '12px',
      fontWeight: 'bold',
      padding: '12px',
    },
  },
  {
    id: '7',
    data: { label: 'Process Complete' },
    position: { x: 700, y: 330 },
    style: {
      background: '#18181b',
      color: 'white',
      border: '1px solid #7c3aed',
      borderRadius: '50%',
      fontSize: '12px',
      fontWeight: 'bold',
      padding: '20px',
      width: '120px',
      height: '120px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      textAlign: 'center',
    },
  },
  {
    id: '8',
    data: { label: 'Send content via Email' },
    position: { x: 150, y: 450 },
    style: {
      background: '#18181b',
      color: 'white',
      border: '1px solid #7c3aed',
      borderRadius: '12px',
      fontSize: '12px',
      fontWeight: 'bold',
      padding: '12px',
    },
  },
  {
    id: '9',
    data: { label: 'Send to AI Agent for revision' },
    position: { x: 500, y: 500 },
    style: {
      background: '#18181b',
      color: 'white',
      border: '1px solid #7c3aed',
      borderRadius: '12px',
      fontSize: '12px',
      fontWeight: 'bold',
      padding: '12px',
    },
  },
  {
    id: '10',
    data: { label: 'AI Agent recreates content' },
    position: { x: 750, y: 500 },
    style: {
      background: '#18181b',
      color: 'white',
      border: '1px solid #7c3aed',
      borderRadius: '12px',
      fontSize: '12px',
      fontWeight: 'bold',
      padding: '12px',
    },
  },
];

const initialEdges: Edge[] = [
  { id: 'e1-3', source: '1', target: '3', animated: true },
  { id: 'e2-3', source: '2', target: '3', animated: true },
  { id: 'e3-4', source: '3', target: '4', animated: true },
  { id: 'e4-5', source: '4', target: '5', animated: true, label: 'Approve' },
  { id: 'e5-6', source: '5', target: '6', animated: true },
  { id: 'e6-7', source: '6', target: '7', animated: true },
  { id: 'e4-8', source: '4', target: '8', animated: true, label: 'Feedback' },
  { id: 'e4-9', source: '4', target: '9', animated: true, label: 'Feedback' },
  { id: 'e9-10', source: '9', target: '10', animated: true },
  { id: 'e10-3', source: '10', target: '3', animated: true, style: { stroke: '#F59E0B' } },
];

const ProjectThree = () => {
  const [nodes, , onNodesChange] = useNodesState(initialNodes);
  const [edges, , onEdgesChange] = useEdgesState(initialEdges);

  return (
    <ProjectCaseStudy
      title="Notion to LinkedIn Automation"
      tagline="Notion drafts become publish-ready LinkedIn posts, automatically."
      icon={<Cloud className="w-7 h-7" />}
      context="Content lived in Notion drafts, but shipping to LinkedIn meant rewriting, reformatting, and polishing each post manually."
      built="A pipeline that watches Notion drafts, rewrites them into polished LinkedIn-ready posts with AI, applies tone/length rules, and hands off content ready to publish."
      stack={['Notion API', 'OpenAI', 'LinkedIn API', 'Automation']}
      highlights={['Draft-to-post in one pass', 'Consistent tone and format', 'No manual rewrite step']}
      nodes={nodes}
      edges={edges}
      onNodesChange={onNodesChange}
      onEdgesChange={onEdgesChange}
    />
  );
};

export default ProjectThree;